// CARDS DE BUSCA ("tags") — atalhos que o cliente cadastra no CRM em
// /marketing/personalizar-site?tab=tags (tabela tbl_card_busca) e que aparecem logo abaixo da
// busca da home. Até aqui só o hero 1 (welcome/WelcomePieces) renderizava esse bloco; os demais
// recebiam (ou nem recebiam) a prop e ignoravam, então quem cadastrou tag num hero novo nunca
// viu nada no site. Este componente existe para o bloco ser UM só, reusado por todos os heros.
//
// Diferença em relação ao bloco original do hero 1: a COR escolhida no CRM é respeitada. Lá o
// chip é sempre o mesmo vidro translúcido e o campo `cor` do banco é descartado — a tela do CRM
// mostra o chip colorido e o site entregava outra coisa. A cor do texto é CALCULADA a partir do
// fundo (ensureReadableTextColor), o mesmo caminho do botão "Buscar" dos heros, para o contraste
// não depender da cor que a pessoa escolheu.
import Link from 'next/link';
import React from 'react';
import { motion } from 'framer-motion';
import { ensureReadableTextColor, isColorLight, removerAcentos } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import styles from './CardsBusca.module.css';

export interface CardBusca {
    id?: number | null;
    nome?: string | null;
    url?: string | null;
    cor?: string | null;
}

export interface CardsBuscaProps {
    /** vem de props.allCards (SSR já filtra os sem URL e não consulta com a flag desligada). */
    cards?: any;
    site: any;
    /** 'escuro' = hero sobre foto/véu escuro; 'claro' = hero em card branco. */
    variante?: 'claro' | 'escuro';
    /**
     * Rótulo acima dos chips. Serve onde o hero JÁ tem uma fileira de chips com outra função
     * (o hero de IA mostra sugestões de frase): sem rótulo viram duas fileiras idênticas com
     * comportamentos diferentes.
     */
    titulo?: string;
    className?: string;
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const CardsBusca: React.FC<CardsBuscaProps> = ({
    cards,
    site,
    variante = 'claro',
    titulo,
    className,
}) => {
    const lista: CardBusca[] = Array.isArray(cards) ? cards.filter((c: CardBusca) => c?.url) : [];
    if (!lista.length) return null;

    const conteudo = (
        <motion.ul
            className={`${styles.lista} ${variante === 'escuro' ? styles.escuro : styles.claro} ${
                titulo ? styles.comTitulo : ''
            } ${className || ''}`}
            aria-label={titulo || 'Busca rápida'}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.45, ease: 'easeOut' }}
        >
            {lista.map((card, index) => {
                const cor = HEX.test(String(card?.cor || '').trim())
                    ? String(card.cor).trim()
                    : '';
                // Sem cor cadastrada o chip fica no visual neutro da variante (CSS).
                const estilo = cor
                    ? {
                          backgroundColor: cor,
                          borderColor: cor,
                          color:
                              ensureReadableTextColor(
                                  isColorLight(cor) ? site?.cor11 : site?.cor13,
                                  cor
                              ) || (isColorLight(cor) ? '#1a1a1a' : '#ffffff'),
                      }
                    : undefined;

                return (
                    <li className={styles.item} key={card?.id ?? index}>
                        <Link
                            prefetch={false}
                            href={removerAcentos(String(card?.url || '')).toLowerCase()}
                            passHref
                            onClick={() =>
                                trackMetaEvent(site, 'ViewContent', {
                                    content_name: card?.nome,
                                    content_category: 'property_category_shortcut',
                                })
                            }
                        >
                            <span
                                className={styles.chip}
                                style={estilo}
                                title={`Busca personalizada por imóveis: ${card?.nome || ''}`}
                            >
                                {card?.nome}
                            </span>
                        </Link>
                    </li>
                );
            })}
        </motion.ul>
    );

    if (!titulo) return conteudo;

    return (
        <div
            className={`${styles.bloco} ${variante === 'escuro' ? styles.escuro : styles.claro}`}
        >
            <span className={styles.titulo}>{titulo}</span>
            {conteudo}
        </div>
    );
};

export default CardsBusca;
